/**
 * Google Analytics Utility
 * Comprehensive tracking for user interactions
 */

import vendorConfig from '../vendors/vendorConfig';

const GA_MEASUREMENT_ID = vendorConfig.branding.gaTrackingId || 'G-5GX3RG42ZC';

// Check if gtag is available (disabled if no GA tracking ID configured)
const isGtagAvailable = () => {
  if (!vendorConfig.branding.gaTrackingId) return false;
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Track page views
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (!isGtagAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title
  });
};

/**
 * Track custom events
 * @param {string} eventName - Event name
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (!isGtagAvailable()) return;

  window.gtag('event', eventName, params);
};

// ==========================================
// USER AUTHENTICATION EVENTS
// ==========================================

export const trackLogin = (method = 'privy') => {
  trackEvent('login', {
    method,
    event_category: 'authentication'
  });
};

export const trackSignup = (method = 'privy') => {
  trackEvent('sign_up', {
    method,
    event_category: 'authentication'
  });
};

export const trackLogout = () => {
  trackEvent('logout', {
    event_category: 'authentication'
  });
};

// ==========================================
// VISA SEARCH & DISCOVERY EVENTS
// ==========================================

export const trackVisaSearch = (category, destination, citizenship) => {
  trackEvent('visa_search', {
    event_category: 'visa_discovery',
    visa_category: category,
    destination_country: destination,
    citizenship_country: citizenship
  });
};

export const trackVisaView = (visaId, visaName, country) => {
  trackEvent('view_item', {
    event_category: 'visa_discovery',
    item_id: visaId,
    item_name: visaName,
    item_category: 'visa',
    country: country
  });
};

export const trackVisaCardClick = (visaId, visaName, country, source) => {
  trackEvent('select_content', {
    event_category: 'visa_discovery',
    content_type: 'visa_card',
    item_id: visaId,
    item_name: visaName,
    country: country,
    source: source // 'featured', 'search_results', 'all_visas'
  });
};

export const trackFilterChange = (filterType, filterValue) => {
  trackEvent('filter_change', {
    event_category: 'visa_discovery',
    filter_type: filterType, // 'category', 'destination', 'citizenship'
    filter_value: filterValue
  });
};

// ==========================================
// VISA APPLICATION EVENTS
// ==========================================

export const trackApplicationStart = (visaId, visaName, country) => {
  trackEvent('begin_checkout', {
    event_category: 'visa_application',
    item_id: visaId,
    item_name: visaName,
    country: country
  });
};

export const trackApplicationStepComplete = (visaId, stepNumber, stepName) => {
  trackEvent('application_step_complete', {
    event_category: 'visa_application',
    visa_id: visaId,
    step_number: stepNumber,
    step_name: stepName
  });
};

export const trackApplicationSubmit = (visaId, visaName, country, totalPrice) => {
  trackEvent('purchase', {
    event_category: 'visa_application',
    transaction_id: `APP-${Date.now()}`,
    value: totalPrice,
    currency: 'USD',
    items: [{
      item_id: visaId,
      item_name: visaName,
      item_category: 'visa',
      price: totalPrice,
      quantity: 1
    }]
  });
};

export const trackApplicationAbandoned = (visaId, stepNumber, stepName) => {
  trackEvent('application_abandoned', {
    event_category: 'visa_application',
    visa_id: visaId,
    abandoned_at_step: stepNumber,
    step_name: stepName
  });
};

// ==========================================
// DOCUMENT EVENTS
// ==========================================

export const trackDocumentUpload = (documentType, success = true) => {
  trackEvent('document_upload', {
    event_category: 'documents',
    document_type: documentType,
    success: success
  });
};

export const trackDocumentView = (documentType) => {
  trackEvent('document_view', {
    event_category: 'documents',
    document_type: documentType
  });
};

export const trackDocumentDelete = (documentType) => {
  trackEvent('document_delete', {
    event_category: 'documents',
    document_type: documentType
  });
};

// ==========================================
// GLOBAL PASS EVENTS
// ==========================================

export const trackGlobalPassView = () => {
  trackEvent('global_pass_view', {
    event_category: 'global_pass'
  });
};

export const trackProfileUpdate = (field) => {
  trackEvent('profile_update', {
    event_category: 'global_pass',
    field_updated: field
  });
};

export const trackPhotoProcessorOpen = (country) => {
  trackEvent('photo_processor_open', {
    event_category: 'global_pass',
    country: country
  });
};

export const trackPhotoProcessed = (country, success = true) => {
  trackEvent('photo_processed', {
    event_category: 'global_pass',
    country: country,
    success: success
  });
};

// ==========================================
// NAVIGATION EVENTS
// ==========================================

export const trackNavigation = (from, to) => {
  trackEvent('navigation', {
    event_category: 'navigation',
    from_page: from,
    to_page: to
  });
};

export const trackExternalLink = (url, linkText) => {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: linkText,
    link_url: url
  });
};

// ==========================================
// ENGAGEMENT EVENTS
// ==========================================

export const trackScrollDepth = (percentage, pagePath) => {
  trackEvent('scroll', {
    event_category: 'engagement',
    percent_scrolled: percentage,
    page_path: pagePath
  });
};

export const trackTimeOnPage = (seconds, pagePath) => {
  trackEvent('engagement_time', {
    event_category: 'engagement',
    time_seconds: seconds,
    page_path: pagePath
  });
};

export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    event_category: 'engagement',
    button_name: buttonName,
    location: location
  });
};

// ==========================================
// ERROR TRACKING
// ==========================================

export const trackError = (errorType, errorMessage, componentName) => {
  trackEvent('exception', {
    description: `${errorType}: ${errorMessage}`,
    fatal: false,
    component: componentName
  });
};

export const trackApiError = (endpoint, statusCode, errorMessage) => {
  trackEvent('api_error', {
    event_category: 'errors',
    endpoint: endpoint,
    status_code: statusCode,
    error_message: errorMessage
  });
};

// ==========================================
// USER PROPERTIES
// ==========================================

export const setUserProperties = (userId, properties = {}) => {
  if (!isGtagAvailable()) return;

  window.gtag('set', 'user_properties', {
    user_id: userId,
    ...properties
  });
};

export const setUserId = (userId) => {
  if (!isGtagAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId
  });
};

// ==========================================
// E-COMMERCE TRACKING
// ==========================================

export const trackViewItemList = (items, listName) => {
  trackEvent('view_item_list', {
    event_category: 'ecommerce',
    item_list_name: listName,
    items: items.map((item, index) => ({
      item_id: item.id,
      item_name: item.name,
      item_category: 'visa',
      price: item.price,
      index: index
    }))
  });
};

export const trackAddToCart = (visaId, visaName, price) => {
  trackEvent('add_to_cart', {
    event_category: 'ecommerce',
    currency: 'USD',
    value: price,
    items: [{
      item_id: visaId,
      item_name: visaName,
      item_category: 'visa',
      price: price,
      quantity: 1
    }]
  });
};

// ==========================================
// CONVERSION TRACKING
// ==========================================

export const trackConversion = (conversionType, value = 0, currency = 'USD') => {
  trackEvent('conversion', {
    event_category: 'conversions',
    conversion_type: conversionType,
    value: value,
    currency: currency
  });
};

export const trackLeadGeneration = (source, visaType) => {
  trackEvent('generate_lead', {
    event_category: 'conversions',
    source: source,
    visa_type: visaType
  });
};

export default {
  trackPageView,
  trackEvent,
  trackLogin,
  trackSignup,
  trackLogout,
  trackVisaSearch,
  trackVisaView,
  trackVisaCardClick,
  trackFilterChange,
  trackApplicationStart,
  trackApplicationStepComplete,
  trackApplicationSubmit,
  trackApplicationAbandoned,
  trackDocumentUpload,
  trackDocumentView,
  trackDocumentDelete,
  trackGlobalPassView,
  trackProfileUpdate,
  trackPhotoProcessorOpen,
  trackPhotoProcessed,
  trackNavigation,
  trackExternalLink,
  trackScrollDepth,
  trackTimeOnPage,
  trackButtonClick,
  trackError,
  trackApiError,
  setUserProperties,
  setUserId,
  trackViewItemList,
  trackAddToCart,
  trackConversion,
  trackLeadGeneration
};
